import { compareCalendarDates, isDateWithin, type CalendarDate } from "../shared/calendar-date";
import type { WeekPlan } from "../planning/week-plan";

export type CheckInState = "active" | "due" | "overdue_locked" | "completed";
export type LoggingIntent = "normal" | "check_in_backfill";
export type WeightLoggingIntent = LoggingIntent | "check_in_measurement";

export function checkInStateOn(plan: WeekPlan, today: CalendarDate): CheckInState {
  if (plan.status === "completed") return "completed";
  const comparison = compareCalendarDates(today, plan.checkInDueDate);
  return comparison < 0 ? "active" : comparison === 0 ? "due" : "overdue_locked";
}

export interface LoggingDecision {
  readonly allowed: boolean;
  readonly reason?: "outside_plan" | "check_in_overdue";
}

/**
 * Once overdue, normal logging is blocked. The check-in flow may still backfill
 * dates in the closing plan; it can never log a new day after that plan.
 */
export function decideLogging(
  plan: WeekPlan,
  today: CalendarDate,
  entryDate: CalendarDate,
  intent: LoggingIntent = "normal",
): LoggingDecision {
  if (!isDateWithin(entryDate, plan.startDate, plan.endDate)) return { allowed: false, reason: "outside_plan" };
  const state = checkInStateOn(plan, today);
  if (state === "overdue_locked" && intent !== "check_in_backfill") {
    return { allowed: false, reason: "check_in_overdue" };
  }
  return { allowed: true };
}

export function assertLoggingAllowed(
  plan: WeekPlan,
  today: CalendarDate,
  entryDate: CalendarDate,
  intent: LoggingIntent = "normal",
): void {
  const decision = decideLogging(plan, today, entryDate, intent);
  if (!decision.allowed) {
    throw new Error(decision.reason === "check_in_overdue" ? "Weekly check-in must be completed before logging" : "Date is outside the current week plan");
  }
}

/**
 * A due/overdue check-in may record one real measurement for its actual
 * completion date, even though that date is after the closing allocation week.
 * This exception is deliberately weight-only and cannot be used by food logs.
 */
export function assertWeightLoggingAllowed(
  plan: WeekPlan,
  today: CalendarDate,
  entryDate: CalendarDate,
  intent: WeightLoggingIntent = "normal",
): void {
  if (intent !== "check_in_measurement") {
    assertLoggingAllowed(plan, today, entryDate, intent);
    return;
  }
  const state = checkInStateOn(plan, today);
  if (entryDate !== today || (state !== "due" && state !== "overdue_locked")) {
    throw new Error("A check-in measurement must be recorded on the due or overdue completion date");
  }
}
